import { getAntiforgeryToken, setAntiforgeryToken } from "../../api/http";

// Formularios de cuenta (T17 y T18) contra el servidor del portal nuevo (/api/portal/session/...). El servidor habla con
// Identity y Platform y devuelve el paso siguiente; aquí solo se envía el formulario y se dibuja el resultado.

export type FieldIssue = { field: string; messageKey: string };
export type AccountResult = {
  // IDN-10: signIn (cuenta existente sin su sesión: `next` es el login que vuelve a la invitación) y otherAccount.
  status: "done" | "invalid" | "error" | "redirect" | "signedIn" | "signIn" | "otherAccount";
  next: string | null;
  messageKey: string | null;
  fields: FieldIssue[] | null;
};

/** Token antiforgery de la visita (lo pide antes del primer envío). */
export async function antiforgery(): Promise<string> {
  const current = getAntiforgeryToken();
  if (current) return current;
  const session = (await (await fetch("/api/portal/session", { credentials: "same-origin" })).json()) as {
    antiforgeryToken: string;
  };
  setAntiforgeryToken(session.antiforgeryToken);
  return session.antiforgeryToken;
}

/**
 * IDN-10 (decisión 14): si hay una sesión iniciada en este navegador (también la limitada a una invitación). Deja guardado
 * el token antiforgery de la visita, que usan los envíos siguientes. Sin respuesta legible: sin sesión.
 */
export async function readSignedIn(): Promise<boolean> {
  const response = await fetch("/api/portal/session", { credentials: "same-origin" }).catch(() => null);
  const session = response?.ok
    ? ((await response.json().catch(() => null)) as { authenticated?: boolean; antiforgeryToken?: string } | null)
    : null;
  if (session?.antiforgeryToken) setAntiforgeryToken(session.antiforgeryToken);
  return session?.authenticated === true;
}

/** IDN-10 (decisión 14): cierra la sesión con el mismo POST /logout del menú de la cuenta (revoca en Identity). */
export async function signOut(): Promise<boolean> {
  const response = await fetch("/logout", {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-CSRF-TOKEN": await antiforgery() },
  }).catch(() => null);
  return response?.ok === true;
}

/** POST que devuelve el resultado también con 4xx/5xx (el servidor siempre responde AccountResult). */
async function post(path: string, body: unknown): Promise<AccountResult> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-TOKEN": await antiforgery() },
    body: JSON.stringify(body),
  }).catch(() => null);
  const result = response ? ((await response.json().catch(() => null)) as AccountResult | null) : null;
  // Sin respuesta legible: el mismo aviso genérico que una falla técnica del servidor.
  return result ?? { status: "error", next: null, messageKey: "ERROR_TEXT", fields: null };
}

export const completeReset = (body: { token: string | null; email: string; newPassword: string }) =>
  post("/api/portal/session/recover", body);

export const changePendingPassword = (body: {
  token: string | null;
  currentPassword: string;
  newPassword: string;
  returnUrl: string | null;
}) => post("/api/portal/session/change-password", body);

export const register = (body: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  companyName: string;
  country: string;
  timezone: string;
}) => post("/api/portal/session/register", body);

export const acceptInvitation = (body: {
  token: string | null;
  email: string;
  isNewAccount: boolean;
  firstName: string;
  lastName: string;
  password: string;
}) => post("/api/portal/session/invitation", body);

/** IDN-10: qué mostrar con una cuenta existente; empresa y rol solo llegan con la sesión de la cuenta invitada. */
export type InvitationPreview = {
  status: "ready" | "signIn" | "otherAccount" | "invalid" | "error";
  organizationName: string | null;
  roleCode: string | null;
  loginHref: string | null;
  messageKey: string | null;
};

export async function previewInvitation(token: string): Promise<InvitationPreview> {
  const response = await fetch("/api/portal/session/invitation/preview", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-TOKEN": await antiforgery() },
    body: JSON.stringify({ token }),
  }).catch(() => null);
  const result = response ? ((await response.json().catch(() => null)) as InvitationPreview | null) : null;
  // Sin respuesta legible: falla técnica, nunca se ofrece aceptar.
  return (
    result ?? { status: "error", organizationName: null, roleCode: null, loginHref: null, messageKey: "ERROR_TEXT" }
  );
}

/** Motivo del cambio obligatorio y el enlace de vuelta al login (o, sin token válido, a dónde volver). */
export async function readPendingChange(
  token: string | null,
  returnUrl: string | null,
): Promise<{ reason: string; loginHref: string } | { redirect: string }> {
  const query = new URLSearchParams();
  if (token) query.set("token", token);
  if (returnUrl) query.set("returnUrl", returnUrl);
  const response = await fetch(`/api/portal/session/change-password?${query.toString()}`, {
    credentials: "same-origin",
  });
  const body = (await response.json()) as { reason?: string; loginHref?: string; next?: string | null };
  return response.ok
    ? { reason: body.reason ?? "", loginHref: body.loginHref ?? "/login" }
    : { redirect: body.next ?? "/login" };
}
